import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../Header/Header.tsx";
import Footer from "../Footer/Footer.tsx";
import { Nav, Tab, Tabs, Table, Accordion } from "react-bootstrap";
import classnames from "classnames";
import { Modal, Button, Form, FormGroup, FormLabel, Spinner } from "react-bootstrap";
import GridIcon from "../../../images/grid-icon.png";
import ListIcon from "../../../images/list-icon.svg";
import SearchIcon from "../../../images/search-icon.png";
import GetIcon from "../../../images/get-icon.svg";
import FilterIcon from "../../../images/filter-icon.svg";
import GiftIconMain from "../../../../src/images/icon-gift-main.png";
import InformationImg from "../../../images/category-img-5.png";
import {
  FiMoreVertical,
  FiArrowLeft,
  FiChevronLeft,
  FiChevronRight,
  FiEye,
  FiEdit2,
  FiTrash2,
  FaPlay,
  FaPause
} from "../../../icons";
import SimpleAudioRecorder from "../Gift/Message/SimpleAudioRecorder.tsx";
import { messageApi } from "../Gift/Message/MessageApi.ts";
import { Dropdown } from "react-bootstrap";
import StartGift from "../Gift/StartGift.tsx";
import { isGiftReadyForPayment } from "../Gift/giftCompletion.ts";
import MyContracts from "../MyContracts/MyContracts.tsx";
import "./MyGifts.scss";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import apiConfig from "../config/apiConfig.js";
import {
  giftItemApi,
  UpdateGiftContactRequest,
  GiftItem,
} from "../Gift/Gift/GiftItemApi.ts";
import { contactApi, Contact } from "../MyContracts/contactApi.tsx";
import Swal from "sweetalert2";
import {
  showNavigationConfirmation,
  isGiftCreationRoute,
} from "../Gift/navigationGuard";
import { useGiftContext } from "../Gift/GiftContext";
import GiftBox from "../../../images/gift-new.png";
import NoGiftsFoundImg from "../../../images/no-gifts-found.png";
import AudioWavePlayer from "../Gift/Message/AudioWavePlayer";
import GiftLoader from "../../common/GiftLoader";
import IconAduio from "../../../images/icon-audio-main.png";
import IconMessage from "../../../images/icon-message-main.png";

// Old AudioPlayer removed - now using AudioWavePlayer

// US Address formatting helper function
const formatUSAddress = (address1: string, address2: string, city: string, state: string, zip: string): string => {
  const addressLines: string[] = [];
  
  // Add street address (Address Line 1) - always required
  if (address1?.trim()) {
    addressLines.push(address1.trim());
  }
  
  // Add apartment/suite/unit (Address Line 2) - optional
  if (address2?.trim()) {
    addressLines.push(address2.trim());
  }
  
  // Add City, State ZIP line - format as single line
  const locationParts: string[] = [];
  if (city?.trim()) locationParts.push(city.trim());
  if (state?.trim()) locationParts.push(state.trim());
  
  if (locationParts.length > 0) {
    let cityStateLine = locationParts.join(', ');
    if (zip?.trim()) {
      cityStateLine += ` ${zip.trim()}`;
    }
    addressLines.push(cityStateLine);
  }
  
  // Join with line breaks for proper US address format
  return addressLines.join('\n');
};

// Currency formatting helper function
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(value);
};

// No Gifts Found Component with beautiful animations
const NoGiftsFound: React.FC<{
  isDraft?: boolean;
  searchTerm?: string;
  selectedFilter?: string;
  onClearFilters: () => void;
}> = ({ isDraft = false, searchTerm, selectedFilter, onClearFilters }) => {
  const hasActiveFilters = (searchTerm && searchTerm.trim()) || selectedFilter;
  
  const getNoGiftsMessage = () => {
    if (hasActiveFilters) {
      return {
        title: `No ${isDraft ? "draft " : ""}gifts found`,
        subtitle: searchTerm 
          ? `We couldn't find any gifts matching "${searchTerm}"`
          : "No gifts match your current filters",
        description: "Try adjusting your search terms or clearing filters to see more results."
      };
    }
    
    return {
      title: `No ${isDraft ? "draft " : ""}gifts yet`,
      subtitle: "Your gift collection is waiting to be started",
      description: "Create your first meaningful gift to begin your journey."
    };
  };

  const content = getNoGiftsMessage();

  return (
    <motion.div
      className="no-gifts-found-container"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '3rem 1rem',
        minHeight: '400px'
      }}
    >
      {/* Animated Image */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ 
          delay: 0.2,
          duration: 0.8,
          type: "spring",
          stiffness: 200,
          damping: 15
        }}
        style={{ marginBottom: '0rem' }}
      >
        <motion.img
          src={NoGiftsFoundImg}
          alt="No gifts found"
          animate={{ 
            y: [0, -10, 0],
            rotate: [0, 2, 0, -2, 0]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          style={{
            width: '150px',
            height: 'auto',
            maxWidth: '100%',
            filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.1))'
          }}
        />
      </motion.div>

      {/* Animated Text Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        style={{ maxWidth: '500px' }}
      >
        <motion.h3
          style={{
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#2c3e50',
            marginBottom: '0.5rem',
            lineHeight: '1.3'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.4 }}
        >
          {content.title}
        </motion.h3>
        
        <motion.p
          style={{
            fontSize: '1.1rem',
            color: '#5a6c7d',
            marginBottom: '0.75rem',
            lineHeight: '1.4'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7, duration: 0.4 }}
        >
          {content.subtitle}
        </motion.p>

        <motion.p
          style={{
            fontSize: '0.95rem',
            color: '#7f8c8d',
            marginBottom: '2rem',
            lineHeight: '1.5'
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.4 }}
        >
          {content.description}
        </motion.p>

        {/* Clear Filters Button */}
        {hasActiveFilters && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.9, duration: 0.4 }}
          >
            <motion.button
              className="btn btn-outline-primary btn-lg"
              onClick={onClearFilters}
              whileHover={{ 
                scale: 1.05,
                boxShadow: "0 8px 25px rgba(57, 87, 160, 0.3)",
                transition: { duration: 0.2 }
              }}
              whileTap={{ 
                scale: 0.95,
                transition: { duration: 0.1 }
              }}
              style={{
                borderRadius: '50px',
                padding: '12px 30px',
                fontWeight: '500',
                border: '2px solid #3957A0',
                color: '#3957A0',
                background: 'transparent',
                transition: 'all 0.3s ease'
              }}
            >
              🔍 Clear Search & Filters
            </motion.button>
          </motion.div>
        )}

        {/* Floating particles animation */}
        <div style={{ position: 'relative', height: '100px', overflow: 'hidden' }}>
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              style={{
                position: 'absolute',
                width: '4px',
                height: '4px',
                background: `linear-gradient(45deg, #3957A0, #D7A360, #99CAEB)`,
                borderRadius: '50%',
                left: `${20 + i * 12}%`,
                opacity: 0.3
              }}
              animate={{
                y: [100, -20],
                x: [0, Math.sin(i) * 20],
                opacity: [0, 0.6, 0]
              }}
              transition={{
                duration: 3 + i * 0.5,
                repeat: Infinity,
                delay: i * 0.5,
                ease: "easeOut"
              }}
            />
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Utility function to generate initials from gift title
const generateGiftInitials = (title: string) => {
  if (!title) return "GI"; // Default: Gift Item

  const words = title.trim().split(" ");
  if (words.length >= 2) {
    return `${words[0].charAt(0).toUpperCase()}${words[1]
      .charAt(0)
      .toUpperCase()}`;
  } else if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return "GI";
};

// Component for rendering gift card image or initials
const GiftCardImage: React.FC<{
  member: Member;
  size?: "small" | "large";
  className?: string;
}> = ({ member, size = "large", className = "" }) => {
  // Priority: Contact profile picture > Gift item image > Initials
  const [imageError, setImageError] = useState(false);
  const [profileImageError, setProfileImageError] = useState(false);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleProfileImageError = () => {
    setProfileImageError(true);
  };


  // Try contact profile picture first
  if (member.profileUrl && !profileImageError) {
    return (
      <img
        src={member.profileUrl}
        alt={member.name}
        className={className}
        loading="lazy"
        onError={handleProfileImageError}
        style={{
          width: size === "small" ? "35px" : "105px",
          height: size === "small" ? "35px" : "105px",
          objectFit: "cover",
          borderRadius: size === "small" ? "50%" : "26px",
        }}
      />
    );
  }

  // Fallback to gift item image
  if (member.hasActualImage && member.giftItemImage && !imageError) {
    return (
      <img
        src={member.giftItemImage}
        alt={member.name}
        className={className}
        loading="lazy"
        onError={handleImageError}
        style={{
          width: size === "small" ? "35px" : "105px",
          height: size === "small" ? "35px" : "105px",
          objectFit: "cover",
          borderRadius: size === "small" ? "8px" : "26px",
        }}
      />
    );
  }

  // Final fallback to initials-based avatar
  return (
    <div
      className={`initials-avatar ${className}`}
      style={{
        width: size === "small" ? "35px" : "105px",
        height: size === "small" ? "35px" : "105px",
        background:
          "linear-gradient(135deg, #3957A0 0%, #D7A360 50%, #99CAEB 100%)",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: size === "small" ? "50%" : "26px",
        fontSize: size === "small" ? "14px" : "32px",
        fontWeight: "bold",
        margin: "0 auto",
      }}
    >
      {member.initials}
    </div>
  );
};

// Define interfaces for type safety
interface Gift {
  _id: string;
  title?: string;
  recipientId?: string; // Reference to contact
  recipientSnapshot: {
    name: string;
    email?: string;
    relationship: string;
    profileUrl?: string; // Profile picture URL
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  occasion: string;
  date: string;
  occasionType?: string;
  milestone?: string;
  milestoneEstimateDate?: string;
  deliverySchedule: string;
  numberOfYears?: string | number;
  frequency?: string;
  status?: string;
  type?: string;
  paymentStatus?: string; // Payment status field
  giftSection?: any;
  messages?: any[];
  createdAt?: string;
  updatedAt?: string;
  giftsteps?: string;
  primaryLegacyContact?: any;
}

interface Member {
  name: string;
  role: string;
  type: string;
  date: string;
  frequency: string;
  image: string;
  _id?: string;
  giftItemImage?: string;
  initials?: string;
  hasActualImage?: boolean;
  profileUrl?: string; // Contact profile picture
  recipientId?: string; // Reference to contact
}

interface TableMember extends Member {
  title: string;
}

interface PaginationInfo {
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

type ViewMode = "grid" | "list";
type FilterType =
  | "friend"
  | "family"
  | "socialMedia"
  | "advertisement"
  | "other"
  | "";

// Function to generate initials from recipient name
const generateRecipientInitials = (name: string) => {
  if (!name) return "GU"; // Default: Guest User

  const words = name.trim().split(" ");
  if (words.length >= 2) {
    return `${words[0].charAt(0).toUpperCase()}${words[1]
      .charAt(0)
      .toUpperCase()}`;
  } else if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return "GU";
};

// GiftDetails component integrated within the main component
const GiftDetails: React.FC<{
  gift: Gift;
  onBack: () => void;
  onEdit: (gift: Gift) => void;
  onCheckout: (gift: Gift) => void;
  giftItemsCache: { [key: string]: GiftItem };
}> = ({ gift, onBack, onEdit, onCheckout, giftItemsCache }) => {
  // State to track which messages are expanded
  const [expandedMessages, setExpandedMessages] = useState<{[key: string]: boolean}>({});

  // State for edit modal
  const [editModal, setEditModal] = useState<{
    show: boolean;
    messageIndex: number;
    text: string;
    audioUrl: string;
    audioFile?: File;
    messageId: string;
    year: number;
    newAudioUrl: string; // URL of newly recorded/uploaded audio
  }>({
    show: false,
    messageIndex: -1,
    text: "",
    audioUrl: "",
    messageId: "",
    year: 0,
    newAudioUrl: ""
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  // Helper function to count words in text
  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  // Helper function to truncate text to specified word count
  const truncateText = (text: string, wordLimit: number): string => {
    const words = text.trim().split(/\s+/);
    if (words.length <= wordLimit) {
      return text;
    }
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  // Helper function to toggle message expansion
  const toggleMessageExpansion = (giftId: string, messageIndex: number) => {
    const key = `${giftId}_${messageIndex}`;
    setExpandedMessages(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Edit handlers
  const handleEditMessage = (message: any, index: number) => {
    setEditModal({
      show: true,
      messageIndex: index,
      text: message.text || "",
      audioUrl: message.audioUrl || "",
      messageId: message._id || "",
      year: message.year || 0,
      newAudioUrl: ""
    });
    setIsRecording(false);
  };

  const handleCloseEditModal = () => {
    setEditModal({
      show: false,
      messageIndex: -1,
      text: "",
      audioUrl: "",
      messageId: "",
      year: 0,
      newAudioUrl: ""
    });
    setIsRecording(false);
  };

  const handleTextChange = (text: string) => {
    setEditModal(prev => ({ ...prev, text }));
  };

  const handleRecordingStart = () => {
    setIsRecording(true);
  };

  const handleRecordingStop = () => {
    setIsRecording(false);
  };

  const handleRecordingComplete = (audioBlob: Blob, audioUrl: string) => {
    const audioFile = new File([audioBlob], `message-${Date.now()}.webm`, {
      type: audioBlob.type
    });
    // Update audioUrl to show new audio in "Current Audio" section immediately
    setEditModal(prev => ({ ...prev, audioFile, newAudioUrl: audioUrl, audioUrl: audioUrl }));
    setIsRecording(false);
  };

  const handleReRecordAudio = () => {
    setEditModal(prev => ({ ...prev, audioFile: undefined, newAudioUrl: "" }));
  };

  const handleSaveEdit = async () => {
    if (!gift || !gift._id) return;

    try {
      setIsSaving(true);

      // Use new audio if recorded/uploaded, otherwise keep original
      let finalAudioUrl = editModal.audioUrl;
      if (editModal.audioFile) {
        // Upload the new audio file to S3
        const uploadResponse = await messageApi.uploadAudioFile(editModal.audioFile);
        if (uploadResponse.status === "Success" && uploadResponse.audioUrl) {
          finalAudioUrl = uploadResponse.audioUrl;
        } else {
          throw new Error("Failed to upload audio");
        }
      }

      // Update the messages array
      const updatedMessages = gift.messages.map((msg: any, idx: number) => {
        if (idx === editModal.messageIndex) {
          return {
            year: msg.year,
            text: editModal.text,
            audioUrl: finalAudioUrl
          };
        }
        return {
          year: msg.year,
          text: msg.text,
          audioUrl: msg.audioUrl
        };
      });

      // Call update API
      const response = await messageApi.updateGiftMessages(gift._id, {
        messages: updatedMessages
      });

      if (response.status === "Success") {
        toast.success("Message updated successfully!");

        // Close the modal
        handleCloseEditModal();

        // Reload the page to show updated data
        setTimeout(() => {
          window.location.reload();
        }, 300);
      } else {
        toast.error(response.message || "Failed to update message");
      }
    } catch (error: any) {
      console.error("Error updating message:", error);
      toast.error(error.message || "Failed to update message");
    } finally {
      setIsSaving(false);
    }
  };

  // Safety check - if gift is null or undefined, show error
  if (!gift) {
    return (
      <div className="container bg-white member-area">
        <div className="text-center py-5">
          <h3>Gift not found</h3>
          <p>Unable to load gift details.</p>
          <Button onClick={onBack}>Back to Gifts</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container bg-white member-area">
      <section className="gift-details-sec">
        {/* Back Button */}
        <div className="mb-3 d-flex justify-content-end">
          <Button
            variant="outline-secondary"
            onClick={onBack}
            className="d-flex align-items-center gap-2 border-0 p-0 no-hover"
            style={{ color: "#3957A0", fontWeight: "700" }}
          >
            <FiArrowLeft color="#3957A0"/>
            Back to Gifts
          </Button>
        </div>

        <div className="row">
          <aside className="gift-detail-block col-xl-4 col-lg-5">
            <div className="gift-detail-block-in">
              <div className="gift-profile">
                <figure>
                  {gift.recipientSnapshot?.profileUrl ? (
                    <img
                      src={gift.recipientSnapshot.profileUrl}
                      alt={gift.recipientSnapshot?.name || 'Recipient'}
                      style={{
                        width: "160px",
                        height: "160px",
                        objectFit: "cover",
                        borderRadius: "26px",
                      }}
                      onError={(e) => {
                        // Hide the broken image and show initials instead
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        const initialsDiv =
                          target.nextElementSibling as HTMLDivElement;
                        if (initialsDiv) {
                          initialsDiv.style.display = "flex";
                        }
                      }}
                    />
                  ) : null}
                  {/* Initials fallback - show when no profileUrl or image fails to load */}
                  <div
                    className="initials-avatar"
                    style={{
                      display: gift.recipientSnapshot?.profileUrl
                        ? "none"
                        : "flex",
                      width: "160px",
                      height: "160px",
                      background:
                        "linear-gradient(135deg, #3957A0 0%, #D7A360 50%, #99CAEB 100%)",
                      color: "white",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "2.5rem",
                      fontWeight: "bold",
                      borderRadius: "26px",
                      margin: "0 auto",
                    }}
                  >
                    {generateRecipientInitials(gift.recipientSnapshot?.name || 'Unknown')}
                  </div>
                </figure>
                <h2>{gift.recipientSnapshot?.name || 'Unknown Recipient'}</h2>
                <span>{gift.recipientSnapshot?.relationship || ''}</span>
                <div className="event">
                  <img src={GiftIconMain} alt="gift-icon" />
                  <span>{gift.occasion || gift.milestone || "Unknown"}</span>
                </div>
              </div>
              <div className="d-flex justify-content-between gift-profile-list">
                <aside>
                  <span>
                    {gift.occasion ? "Occasion Date" : "Milestone Date"}
                  </span>
                  <h2>
                    {(() => {
                      const dateStr = gift.date || gift.milestoneEstimateDate;
                      if (!dateStr) return "Unknown";
                      // Check if date string contains a year (4-digit number or ISO format with year)
                      const hasYear = /\d{4}/.test(dateStr);
                      const dateObj = new Date(dateStr);
                      // For Post-Mortem gifts or dates without year, show only month and day
                      const showYear = hasYear && gift.type !== 'Post-Mortem';
                      return dateObj.toLocaleDateString('en-US',
                        showYear
                          ? { month: '2-digit', day: '2-digit', year: 'numeric' }
                          : { month: '2-digit', day: '2-digit' }
                      );
                    })()}
                  </h2>
                </aside>
                <aside>
                  <span>Delivery Schedule</span>
                  <h2>{gift.milestone && gift.milestoneEstimateDate ? "One-Time" : gift.deliverySchedule}</h2>
                </aside>
              </div>
              <div className="gift-detail-details">
                <ul>
                  <li>
                    <span>Phone</span>
                    <p>901-234-5678</p>
                  </li>
                  <li>
                    <span>Email</span>
                    <p>{gift.recipientSnapshot?.email || "N/A"}</p>
                  </li>
                  <li>
                    <span>Address</span>
                    <p>
                      {(() => {
                        if (gift.recipientSnapshot?.address1 || gift.recipientSnapshot?.address2 || gift.recipientSnapshot?.city || gift.recipientSnapshot?.state || gift.recipientSnapshot?.zip) {
                          const formattedAddress = formatUSAddress(
                            gift.recipientSnapshot?.address1 || '',
                            gift.recipientSnapshot?.address2 || '',
                            gift.recipientSnapshot?.city || '',
                            gift.recipientSnapshot?.state || '',
                            gift.recipientSnapshot?.zip || ''
                          );
                          const addressLines = formattedAddress.split('\n');
                          return addressLines.map((line, index) => (
                            <span key={index}>
                              {line}
                              {index < addressLines.length - 1 && <br />}
                            </span>
                          ));
                        }
                        return "N/A";
                      })()}
                    </p>
                  </li>
                </ul>
              </div>
            </div>
            {gift.giftSection && (
              <div className="gift-detail-block-in gift-info mt-3 review-block">
                <div className="review-block-in p-0">
                  <h3>Gift Information</h3>
                  <div className="table-wrapper">
                    <aside className="information-left">
                      <figure>
                        {/* Use gift section image URL directly from gift data */}
                        <img
                          src={(() => {
                            // Get image URL directly from gift section data
                            const directImageUrl = gift.giftSection?.physicalItem?.imageUrl ||
                                                  gift.giftSection?.giftCard?.imageUrl ||
                                                  gift.giftSection?.monetary?.imageUrl;
                            
                            // Fallback to cached gift item data if direct URL not available
                            const giftItemId = gift.giftSection?.itemRef;
                            const cachedGiftItem = giftItemId ? giftItemsCache[giftItemId] : null;
                            const cacheImageUrl = cachedGiftItem?.imageUrl;
                            
                            const finalImageUrl = directImageUrl || cacheImageUrl;
                            
                            
                            return finalImageUrl || InformationImg;
                          })()}
                          alt="Gift Item"
                          onError={(e) => {
                            // Fallback to generic image if gift item image fails
                            const target = e.target as HTMLImageElement;
                            if (target.src !== InformationImg) {
                              target.src = InformationImg;
                            }
                          }}
                        />
                      </figure>
                    </aside>
                    <aside className="information-right">
                      <h4>
                        {gift.giftSection?.monetary?.title ||
                          gift.giftSection?.giftCard?.title ||
                          gift.giftSection?.physicalItem?.title ||
                          "Gift Item"}
                      </h4>
                      <span>
                        {(() => {
                          const amount = gift.giftSection?.monetary?.amount ||
                                        gift.giftSection?.giftCard?.selectedAmount ||
                                        gift.giftSection?.physicalItem?.price;
                          return amount ? formatCurrency(amount) : "$0.00";
                        })()}
                      </span>
                      <p>
                        {gift.giftSection?.physicalItem?.description ||
                          ""}
                      </p>
                    </aside>
                  </div>
                </div>
              </div>
            )}
          </aside>
          <aside className="gift-detail-cont col-xl-8 col-lg-7">
            <motion.div 
              className="review-block messages-block mb-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            >
              <div className="review-block-in messages-container">
                {/* Enhanced Header */}
                <motion.div 
                  className="messages-header mb-4"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                >
                  <div className="d-flex align-items-center justify-content-between">
                    <div>
                      <h3 style={{ 
                        margin: 0, 
                        color: '#3957A0', 
                        fontWeight: '700',
                        fontSize: '24px' 
                      }}>
                        Messages
                      </h3>
                      <p style={{ 
                        margin: 0, 
                        color: '#677289', 
                        fontSize: '14px',
                        fontWeight: '500' 
                      }}>
                        {gift.messages?.length || 0} message{(gift.messages?.length || 0) !== 1 ? 's' : ''} recorded
                      </p>
                    </div>
                    
                    {/* Message count badge */}
                    {gift.messages && gift.messages.length > 0 && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                        style={{
                          background: 'linear-gradient(135deg, #D7A360 0%, #f4d03f 100%)',
                          color: 'white',
                          padding: '8px 16px',
                          borderRadius: '25px',
                          fontSize: '12px',
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          boxShadow: '0 4px 15px rgba(215, 163, 96, 0.3)'
                        }}
                      >
                        {gift.messages.length} Messages
                      </motion.div>
                    )}
                  </div>
                </motion.div>

                {/* Messages List */}
                <AnimatePresence>
                  {gift.messages && gift.messages.length > 0 ? (
                    <div className="messages-list">
                      {gift.messages.length === 1 ? (
                        // Single message - render directly without accordion
                        gift.messages.map((message, index) => {
                          const wordCount = message.text ? countWords(message.text) : 0;
                          const messageKey = `${gift._id}_${index}`;
                          const isExpanded = expandedMessages[messageKey] || false;
                          const displayText = message.text && wordCount > 200 && !isExpanded
                            ? truncateText(message.text, 200)
                            : message.text;

                          return (
                            <div
                              key={index}
                              className="message-item"
                              style={{
                                marginBottom: '20px',
                                position: 'relative'
                              }}
                            >
                              {/* Message Content */}
                              <div className="message-content">
                                {/* Audio Player Section - appears first */}
                                {message.audioUrl ? (
                                  <div className="audio-section mb-3">
                                    <AudioWavePlayer
                                      audioUrl={message.audioUrl}
                                      showDownload={false}
                                      showVolumeIcon={false}
                                      height={70}
                                      waveColor="#E7ECF1"
                                      progressColor="#3957A0"
                                      cursorColor="#D7A360"
                                    />
                                  </div>
                                ) : (
                                  <div style={{
                                    textAlign: 'center',
                                    padding: '20px',
                                    background: 'rgba(57, 87, 160, 0.05)',
                                    borderRadius: '12px',
                                    border: '2px dashed rgba(57, 87, 160, 0.2)',
                                    marginBottom: '12px'
                                  }}>
                                    {gift.status === 'Draft' ? (
                                      <div>
                                        <div style={{
                                          color: '#3957A0',
                                          marginBottom: '8px',
                                          fontWeight: '600'
                                        }}>
                                          🎙️ Audio recording pending
                                        </div>
                                        <small style={{ color: '#677289' }}>
                                          Click "Edit" to add audio for this message
                                        </small>
                                      </div>
                                    ) : (
                                      <small style={{
                                        color: '#A5A5A5',
                                        fontStyle: 'italic'
                                      }}>
                                        No audio available for this message
                                      </small>
                                    )}
                                  </div>
                                )}

                                {/* Edit Button - shows when message is editable */}
                                {message.editable && (
                                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                                    <Button
                                      variant="outline-primary"
                                      size="sm"
                                      onClick={() => handleEditMessage(message, index)}
                                      style={{
                                        borderRadius: '8px',
                                        padding: '6px 16px',
                                        fontSize: '14px',
                                        fontWeight: '600'
                                      }}
                                    >
                                      <FiEdit2 className="me-1" style={{ fontSize: '14px' }} />
                                      Edit
                                    </Button>
                                  </div>
                                )}

                                {/* Message Text - appears after audio */}
                                {message.text && (
                                  <div className="message-text mb-3">
                                    <div style={{
                                      fontSize: '16px',
                                      lineHeight: '1.6',
                                      color: '#382F2D',
                                      fontWeight: '400',
                                      background: 'rgba(57, 87, 160, 0.02)',
                                      padding: '5px',
                                      borderRadius: '12px',
                                      fontStyle: 'italic'
                                    }}>
                                      "{displayText}"
                                    </div>
                                    {wordCount > 200 && (
                                      <button
                                        onClick={() => toggleMessageExpansion(gift._id, index)}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          color: '#3957A0',
                                          cursor: 'pointer',
                                          fontSize: '14px',
                                          fontWeight: '600',
                                          marginTop: '8px',
                                          padding: '0',
                                          textDecoration: 'underline'
                                        }}
                                      >
                                        {isExpanded ? 'Read Less' : 'Read More'}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        // Multiple messages - render with accordion
                        <Accordion defaultActiveKey="0">
                          {gift.messages.map((message, index) => (
                            <Accordion.Item
                              eventKey={index.toString()}
                              key={index}
                              style={{
                                marginBottom: '15px',
                                border: '1px solid rgba(57, 87, 160, 0.15)',
                                borderRadius: '12px',
                                overflow: 'hidden'
                              }}
                            >
                              <Accordion.Header
                                style={{
                                  background: 'rgba(57, 87, 160, 0.05)',
                                }}
                              >
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  width: '100%'
                                }}>
                                  <span style={{
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#3957A0'
                                  }}>
                                    {message.year ?
                                      // For Post-Mortem gifts, show "Year 1", "Year 2", etc.
                                      gift.type === 'Post-Mortem'
                                        ? `Year ${index + 1}`
                                        : `Year ${message.year}`
                                      : `Message ${index + 1}`
                                    }
                                  </span>
                                  <span style={{
                                    fontSize: '12px',
                                    color: '#677289',
                                    marginLeft: 'auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    paddingRight: '30px'
                                  }}>
                                    {message.text && (
                                      <img src={IconMessage} alt="Message" style={{ width: '16px', height: '16px' }} />
                                    )}
                                    {message.audioUrl && (
                                      <img src={IconAduio} alt="Audio" style={{ width: '16px', height: '16px' }} />
                                    )}
                                    {message.editable && (
                                      <Button
                                        variant="link"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation(); // Prevent accordion toggle
                                          handleEditMessage(message, index);
                                        }}
                                        style={{
                                          padding: '0 8px',
                                          marginLeft: '8px',
                                          color: '#3957A0',
                                          textDecoration: 'none'
                                        }}
                                        title="Edit message"
                                      >
                                        <FiEdit2 style={{ width: '16px', height: '16px' }} />
                                      </Button>
                                    )}
                                  </span>
                                </div>
                              </Accordion.Header>
                              <Accordion.Body style={{ padding: '20px' }}>
                                <div className="message-content">
                                  {/* Audio Player Section - appears first */}
                                  {message.audioUrl ? (
                                    <div className="audio-section mb-3">
                                      <AudioWavePlayer
                                        audioUrl={message.audioUrl}
                                        showDownload={false}
                                        showVolumeIcon={false}
                                        height={70}
                                        waveColor="#E7ECF1"
                                        progressColor="#3957A0"
                                        cursorColor="#D7A360"
                                      />
                                    </div>
                                  ) : (
                                    <div style={{
                                      textAlign: 'center',
                                      padding: '20px',
                                      background: 'rgba(57, 87, 160, 0.05)',
                                      borderRadius: '12px',
                                      border: '2px dashed rgba(57, 87, 160, 0.2)',
                                      marginBottom: '12px'
                                    }}>
                                      {gift.status === 'Draft' ? (
                                        <div>
                                          <div style={{
                                            color: '#3957A0',
                                            marginBottom: '8px',
                                            fontWeight: '600'
                                          }}>
                                            🎙️ Audio recording pending
                                          </div>
                                          <small style={{ color: '#677289' }}>
                                            Click "Edit" to add audio for this message
                                          </small>
                                        </div>
                                      ) : (
                                        <small style={{
                                          color: '#A5A5A5',
                                          fontStyle: 'italic'
                                        }}>
                                          No audio available for this message
                                        </small>
                                      )}
                                    </div>
                                  )}

                                  {/* Message Text - appears after audio */}
                                  {message.text && (() => {
                                    const wordCount = countWords(message.text);
                                    const messageKey = `${gift._id}_${index}`;
                                    const isExpanded = expandedMessages[messageKey] || false;
                                    const displayText = wordCount > 200 && !isExpanded
                                      ? truncateText(message.text, 200)
                                      : message.text;

                                    return (
                                      <div className="message-text mb-3">
                                        <div style={{
                                          fontSize: '16px',
                                          lineHeight: '1.6',
                                          color: '#382F2D',
                                          fontWeight: '400',
                                          background: 'rgba(57, 87, 160, 0.02)',
                                          padding: '5px',
                                          borderRadius: '12px',
                                          fontStyle: 'italic'
                                        }}>
                                          "{displayText}"
                                        </div>
                                        {wordCount > 200 && (
                                          <button
                                            onClick={() => toggleMessageExpansion(gift._id, index)}
                                            style={{
                                              background: 'none',
                                              border: 'none',
                                              color: '#3957A0',
                                              cursor: 'pointer',
                                              fontSize: '14px',
                                              fontWeight: '600',
                                              marginTop: '8px',
                                              padding: '0',
                                              textDecoration: 'underline'
                                            }}
                                          >
                                            {isExpanded ? 'Read Less' : 'Read More'}
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </Accordion.Body>
                            </Accordion.Item>
                          ))}
                        </Accordion>
                      )}
                    </div>
                  ) : (
                    <motion.div
                      className="no-messages"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3, duration: 0.5 }}
                      style={{
                        textAlign: 'center',
                        padding: '60px 20px',
                        background: 'linear-gradient(135deg, rgba(247, 250, 255, 0.8) 0%, rgba(239, 244, 255, 0.8) 100%)',
                        borderRadius: '20px',
                        border: '2px dashed rgba(57, 87, 160, 0.2)'
                      }}
                    >
                      <motion.div
                        animate={{ 
                          y: [0, -10, 0],
                          rotate: [0, 5, 0, -5, 0]
                        }}
                        transition={{ 
                          duration: 4, 
                          repeat: Infinity, 
                          ease: "easeInOut" 
                        }}
                        style={{ fontSize: '64px', marginBottom: '20px' }}
                      >
                        📝
                      </motion.div>
                      <h4 style={{ 
                        color: '#3957A0', 
                        marginBottom: '10px',
                        fontWeight: '600' 
                      }}>
                        No Messages Yet
                      </h4>
                      <p style={{ 
                        color: '#677289', 
                        fontSize: '16px',
                        maxWidth: '300px',
                        margin: '0 auto'
                      }}>
                        Messages will appear here once they are added to this gift
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Delivery Information */}
                <motion.div 
                  className="delivery-info mt-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                  style={{
                    background: 'linear-gradient(135deg, rgba(215, 163, 96, 0.1) 0%, rgba(215, 163, 96, 0.05) 100%)',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '1px solid rgba(215, 163, 96, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px'
                  }}
                >
                  <motion.div
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    style={{
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #D7A360 0%, #f4d03f 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '18px',
                      flexShrink: 0
                    }}
                  >
                    📧
                  </motion.div>
                  <div style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      color: '#8B7355',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      marginBottom: '4px'
                    }}>
                      Delivery Address
                    </div>
                    <div style={{
                      fontSize: '16px',
                      color: '#D7A360',
                      fontWeight: '700',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word'
                    }}>
                      {gift.recipientSnapshot?.email || "N/A"}
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
            {gift.type === "Post-Mortem" && gift.primaryLegacyContact && (
              <div className="review-block">
                <div className="review-block-in">
                  <h3>Legacy Contact Information</h3>
                  <div className="gift-detail-details m-0 p-0">
                    <ul>
                      <li>
                        <span>Name</span>
                        <p>{gift.primaryLegacyContact?.name || "N/A"}</p>
                      </li>
                      <li>
                        <span>Phone</span>
                        <p>{gift.primaryLegacyContact?.phoneNumber || "N/A"}</p>
                      </li>
                      <li>
                        <span>Email</span>
                        <p>{gift.primaryLegacyContact?.email || "N/A"}</p>
                      </li>
                      <li>
                        <span>Address</span>
                        <p>
                          {(() => {
                            if (gift.primaryLegacyContact?.address1 || gift.primaryLegacyContact?.address2 || gift.primaryLegacyContact?.city || gift.primaryLegacyContact?.state || gift.primaryLegacyContact?.zip) {
                              const formattedAddress = formatUSAddress(
                                gift.primaryLegacyContact?.address1 || '',
                                gift.primaryLegacyContact?.address2 || '',
                                gift.primaryLegacyContact?.city || '',
                                gift.primaryLegacyContact?.state || '',
                                gift.primaryLegacyContact?.zip || ''
                              );
                              const addressLines = formattedAddress.split('\n');
                              return addressLines.map((line, index) => (
                                <span key={index} style={{lineHeight: '25px'}}>
                                  {line}
                                  {index < addressLines.length - 1 && <br />}
                                </span>
                              ));
                            }
                            return "N/A";
                          })()}
                        </p>
                      </li>
                      <li>
                        <span>Relationship</span>
                        <p>{gift.primaryLegacyContact?.relationship || "N/A"}</p>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            {/* Hide these actions when payment is completed */}
            {gift.paymentStatus !== "Paid" && (
              <div
                className="mt-3 d-flex gap-2"
                style={{ justifyContent: "flex-end" }}
              >
                {isGiftReadyForPayment(gift) ? (
                  <>
                    <Button
                      className="blue-outline-bttn"
                      onClick={() => onEdit(gift)}
                    >
                      Edit
                    </Button>
                    <Button
                      className="main-bttn w-auto"
                      onClick={() => onCheckout(gift)}
                    >
                      Review &amp; Complete Payment
                    </Button>
                  </>
                ) : (
                  <Button
                    className="blue-outline-bttn"
                    onClick={() => onEdit(gift)}
                  >
                    Continue Editing
                  </Button>
                )}
              </div>
            )}
          </aside>
        </div>
      </section>

      {/* Edit Message Modal */}
      <Modal show={editModal.show} onHide={handleCloseEditModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Message & Audio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-info mb-4">
            <strong>Note:</strong> You are editing the audio and message for Year {editModal.year}.
            Make your changes below and click "Save Changes" to update.
          </div>

          {/* Audio Section */}
          <div className="audio-section mb-4">
            <FormLabel className="d-block mb-2">Audio *</FormLabel>

            {/* Show current audio - hide when recording */}
            {editModal.audioUrl && !isRecording && (
              <div className="current-audio mb-3" style={{
                background: 'rgba(57, 87, 160, 0.05)',
                borderRadius: '12px',
                padding: '16px',
                border: '1px solid rgba(57, 87, 160, 0.2)'
              }}>
                <small className="d-block mb-2" style={{ color: '#677289', fontWeight: '600' }}>
                  Current Audio:
                </small>
                <AudioWavePlayer
                  audioUrl={editModal.audioUrl}
                  showDownload={false}
                  showVolumeIcon={false}
                  height={70}
                  waveColor="#E7ECF1"
                  progressColor="#3957A0"
                  cursorColor="#D7A360"
                />
              </div>
            )}

            {/* Simple audio recorder - just like Step 2 */}
            <div className="audio-recorder">
              <small className="d-block mb-2" style={{ color: '#3957A0', fontWeight: '600' }}>
                {editModal.audioUrl ? 'Record or Upload New Audio:' : 'Record or Upload Audio:'}
              </small>
              <div className="audio-recorder-wrapper">
                <SimpleAudioRecorder
                  key={`audio-recorder-${editModal.messageIndex}`}
                  onRecordingComplete={handleRecordingComplete}
                  onRecordingStart={handleRecordingStart}
                  onRecordingStop={handleRecordingStop}
                  disabled={isSaving}
                  className="mb-3"
                />
              </div>
              <style>{`
                .audio-recorder-wrapper .waveform-container {
                  display: none !important;
                }
              `}</style>
            </div>
          </div>

          {/* Message Text Section */}
          <div className="message-text-section">
            <FormLabel className="d-block mb-2">Message *</FormLabel>
            <textarea
              className="form-control"
              value={editModal.text}
              onChange={(e) => handleTextChange(e.target.value)}
              placeholder="Enter your message..."
              maxLength={2000}
              rows={6}
              disabled={isSaving}
              style={{
                minHeight: "120px",
                resize: "vertical",
                fontFamily: "inherit",
                fontSize: "14px",
                lineHeight: "1.5"
              }}
            />
            <small className="text-muted">
              {editModal.text.length}/2000 characters
            </small>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseEditModal} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSaveEdit} disabled={isSaving}>
            {isSaving ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

const GiftsMessages: React.FC = () => {
  const [giftTab, setGiftTab] = useState<string>("1");
  const [selectedFilter, setSelectedFilter] = useState<FilterType>("");
  const [filterValue, setFilterValue] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  // State to track which messages are expanded
  const [expandedMessages, setExpandedMessages] = useState<{[key: string]: boolean}>({});

  // Helper function to count words in text
  const countWords = (text: string): number => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  // Helper function to truncate text to specified word count
  const truncateText = (text: string, wordLimit: number): string => {
    const words = text.trim().split(/\s+/);
    if (words.length <= wordLimit) {
      return text;
    }
    return words.slice(0, wordLimit).join(' ') + '...';
  };

  // Helper function to toggle message expansion
  const toggleMessageExpansion = (giftId: string, messageIndex: number) => {
    const key = `${giftId}_${messageIndex}`;
    setExpandedMessages(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Navigation for basic routing
  const navigate = useNavigate();
  const location = useLocation();

  // Extract gift ID from URL if present
  const urlParams = location.pathname.split("/");
  const giftIdFromUrl =
    urlParams.length > 2 && urlParams[1] === "my-gifts" ? urlParams[2] : null;

  // Determine active tab based on current route
  const activeTab =
    location.pathname === "/my-contacts"
      ? "2"
      : location.pathname === "/my-contracts"
      ? "3"
      : "1";

  // Determine if we're showing gift details
  const isShowingGiftDetails = !!giftIdFromUrl;

  // API State
  const [allGifts, setAllGifts] = useState<Gift[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);

  // Pagination state
  const [pagination, setPagination] = useState<PaginationInfo>({
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
  });
  const itemsPerPage = 20;

  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [giftItemsCache, setGiftItemsCache] = useState<{
    [key: string]: GiftItem;
  }>({});
  const [contactsCache, setContactsCache] = useState<{
    [key: string]: Contact;
  }>({});
  const [showGigStart, setShowGigStart] = useState<boolean>(false);
  const [dropdownStates, setDropdownStates] = useState<{
    [key: string]: boolean;
  }>({});
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // New state for showing gift details
  const [showGiftDetails, setShowGiftDetails] = useState<boolean>(false);
  const [selectedGiftDetails, setSelectedGiftDetails] = useState<Gift | null>(
    null
  );

  const dropdownRef = useRef<HTMLDivElement>(null);

  // API Functions - Fetch all gifts and categorize on frontend
  const fetchGifts = useCallback(
    async (page: number = 1) => {
      try {
        setLoading(true);
        const authToken = localStorage.getItem("authToken");


        // Determine status based on current tab
        let statusParam = "";
        switch (giftTab) {
          case "1": // My Gifts tab - send "Scheduled"
            statusParam = "Scheduled";
            break;
          case "2": // Draft tab - send "Draft"
            statusParam = "Draft";
            break;
          case "3": // Past tab - send "Delivered"
            statusParam = "Delivered";
            break;
          default:
            statusParam = ""; // No filter, get all
        }

        // Build params object
        const params: any = {
          page: page,
          limit: itemsPerPage,
          search: searchTerm,
        };

        // Add status parameter if we have one
        if (statusParam) {
          params.status = statusParam;
        }


        // Fetch gifts with status filter
        const response = await axios.get(
          `${apiConfig.apiBaseURL}/gift/get-all-gifts`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
            },
            params: params,
          }
        );

        if (response.data.status === "Success") {
          const gifts = response.data.data;

          // Store the filtered gifts from backend
          setAllGifts(gifts);

          // Update pagination info
          if (response.data.pagination) {
            setPagination({
              totalCount: response.data.pagination.totalCount,
              totalPages: response.data.pagination.totalPages,
              currentPage: response.data.pagination.currentPage,
            });
          }
        } else {
          console.error("❌ API Error:", response.data.message);
          toast.error(response.data.message || "Failed to fetch gifts");
          setAllGifts([]);
          setPagination({ totalCount: 0, totalPages: 0, currentPage: 1 });
        }
      } catch (error) {
        console.error("💥 Fetch Gifts Error:", error);
        toast.error("Failed to fetch gifts. Please try again.");
        setAllGifts([]);
        setPagination({ totalCount: 0, totalPages: 0, currentPage: 1 });
      } finally {
        setLoading(false);
        setIsInitialLoad(false); // Mark that initial load is complete
      }
    },
    [giftTab, searchTerm, itemsPerPage]
  );

  // Handle page change
  const handlePageChange = useCallback(
    (newPage: number) => {
      if (
        newPage >= 1 &&
        newPage <= pagination.totalPages &&
        newPage !== pagination.currentPage
      ) {
        setPagination((prev) => ({ ...prev, currentPage: newPage }));
        fetchGifts(newPage);
      }
    },
    [pagination.totalPages, pagination.currentPage, fetchGifts]
  );

  // Get gifts for current tab - frontend categorization
  const getCurrentGifts = useCallback(() => {

    // Since backend already filtered by status, just return all gifts
    return [...allGifts];
  }, [giftTab, allGifts]);

  // Function to fetch gift item details
  const fetchGiftItemDetails = useCallback(
    async (giftItemId: string): Promise<GiftItem | null> => {
      if (giftItemsCache[giftItemId]) {
        return giftItemsCache[giftItemId];
      }

      try {
        const response = await giftItemApi.getSingleGiftItem(giftItemId);
        if (response.status === "Success" && response.data) {
          const giftItem = Array.isArray(response.data)
            ? response.data[0]
            : response.data;
          setGiftItemsCache((prev) => ({ ...prev, [giftItemId]: giftItem }));
          return giftItem;
        }
      } catch (error) {
        console.error("Error fetching gift item details:", error);
      }
      return null;
    },
    [giftItemsCache]
  );

  // Function to fetch contact details
  const fetchContactDetails = useCallback(
    async (contactId: string): Promise<Contact | null> => {
      if (contactsCache[contactId]) {
        return contactsCache[contactId];
      }

      try {
        const response = await contactApi.getContactById(contactId);
        if (response.status === "Success" && response.contact) {
          setContactsCache((prev) => ({
            ...prev,
            [contactId]: response.contact!,
          }));
          return response.contact;
        }
      } catch (error) {
        console.error("📞 Error fetching contact details:", error);
      }
      return null;
    },
    [contactsCache]
  );

  // Convert Gift to Member format for display
  const convertGiftToMember = (gift: Gift): Member => {
    const giftTitle =
      gift.title || `Gift for ${gift.recipientSnapshot?.name || "Unknown"}`;
    const initials = generateRecipientInitials(
      gift.recipientSnapshot?.name || ""
    );

    // Get cached gift item if available
    const giftItemId = gift.giftSection?.itemRef;
    const cachedGiftItem = giftItemId ? giftItemsCache[giftItemId] : null;

    // Get profile URL directly from recipientSnapshot
    const profileUrl = gift.recipientSnapshot?.profileUrl;


    return {
      _id: gift._id,
      name: gift.recipientSnapshot?.name || "Unknown",
      role: gift.recipientSnapshot?.relationship || "Unknown",
      type: gift.occasion || gift.milestone || "Unknown",
      date: (() => {
        const dateStr = gift.date || gift.milestoneEstimateDate;
        if (!dateStr) return "Unknown";
        const hasYear = /\d{4}/.test(dateStr);
        const dateObj = new Date(dateStr);
        return dateObj.toLocaleDateString('en-US',
          hasYear
            ? { month: '2-digit', day: '2-digit', year: 'numeric' }
            : { month: '2-digit', day: '2-digit' }
        );
      })(),
      frequency: gift.milestone && gift.milestoneEstimateDate ? "One-Time" : (gift.deliverySchedule || "Unknown"),
      image: InformationImg, // Keep original fallback for compatibility
      giftItemImage: cachedGiftItem?.imageUrl,
      initials: initials,
      hasActualImage: !!cachedGiftItem?.imageUrl,
      profileUrl:
        profileUrl && profileUrl.trim() !== "" ? profileUrl : undefined,
      recipientId: gift.recipientId,
    };
  };

  // Convert Gift to TableMember format for display
  const convertGiftToTableMember = (gift: Gift): TableMember => ({
    ...convertGiftToMember(gift),
    title:
      gift.title || `Gift for ${gift.recipientSnapshot?.name || "Unknown"}`,
  });

  // Load gift details when URL has gift ID
  useEffect(() => {
    if (giftIdFromUrl && !showGiftDetails) {
      const loadGiftDetails = async () => {
        try {
          const authToken = localStorage.getItem("authToken");
          const response = await axios.get(
            `${apiConfig.apiBaseURL}/gift/get-single-gift/${giftIdFromUrl}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
              },
            }
          );

          if (response.data.status === "Success") {
            setSelectedGiftDetails(response.data.data);
            setShowGiftDetails(true);
          } else {
            toast.error("Failed to load gift details");
            navigate("/my-gifts"); // Redirect back if gift not found
          }
        } catch (error) {
          console.error("Error fetching gift details:", error);
          toast.error("Failed to load gift details");
          navigate("/my-gifts"); // Redirect back on error
        }
      };

      loadGiftDetails();
    } else if (!giftIdFromUrl && showGiftDetails) {
      // Clear gift details if no ID in URL
      setShowGiftDetails(false);
      setSelectedGiftDetails(null);
    }
  }, [giftIdFromUrl, showGiftDetails, navigate]);

  // Load gifts on component mount and when dependencies change
  useEffect(() => {
    if (!isShowingGiftDetails) {
      // Only fetch gifts list if not showing details
      setPagination((prev) => ({ ...prev, currentPage: 1 })); // Reset to page 1 when tab changes
      fetchGifts(1);
    }
  }, [giftTab, isShowingGiftDetails]);

  // Handle search with debouncing
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setPagination((prev) => ({ ...prev, currentPage: 1 })); // Reset to page 1 when searching
      fetchGifts(1);
    }, 500); // 500ms debounce

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  // Fetch gift item details and contact details when gifts are loaded
  useEffect(() => {
    const fetchAllDetails = async () => {
      // Fetch gift item details
      const giftItemIds = allGifts
        .map((gift) => gift.giftSection?.itemRef)
        .filter((id): id is string => !!id);

      for (const giftItemId of giftItemIds) {
        if (!giftItemsCache[giftItemId]) {
          await fetchGiftItemDetails(giftItemId);
        }
      }

      // Note: Profile pictures are now available directly in recipientSnapshot.profileUrl
      // No need to fetch from contacts API
    };

    if (allGifts.length > 0) {
      fetchAllDetails();
    }
  }, [
    allGifts,
    fetchGiftItemDetails,
    fetchContactDetails,
    giftItemsCache,
    contactsCache,
  ]);

  // Handle gift details navigation
  const handleShowGiftDetails = useCallback(
    async (giftOrMember: Gift | Member): Promise<void> => {
      if ("_id" in giftOrMember && typeof giftOrMember._id === "string") {
        // Navigate to the gift detail URL
        navigate(`/my-gifts/${giftOrMember._id}`);
      }
    },
    [navigate]
  );

  const handleBackToGifts = useCallback((): void => {
    navigate("/my-gifts");
  }, [navigate]);

  // Memoized callbacks for performance
  const toggleGiftTab = useCallback(
    (tab: string): void => {
      if (giftTab !== tab) {
        setGiftTab(tab);
        // Reset gift details when changing tabs
        setShowGiftDetails(false);
        setSelectedGiftDetails(null);
      }
    },
    [giftTab]
  );

  // Handle clicking outside dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if any dropdown is open
      const openDropdowns = Object.keys(dropdownStates).filter(
        (key) => dropdownStates[key]
      );
      if (openDropdowns.length > 0) {
        let clickedInsideAnyDropdown = false;

        // Check each open dropdown
        for (const dropdownId of openDropdowns) {
          const currentDropdown = dropdownRefs.current[dropdownId];
          if (
            currentDropdown &&
            currentDropdown.contains(event.target as Node)
          ) {
            clickedInsideAnyDropdown = true;
            break;
          }
        }

        // If clicked outside all dropdowns, close them all
        if (!clickedInsideAnyDropdown) {
          setDropdownStates({});
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownStates]);

  const toggleDropdown = useCallback((giftId: string): void => {
    setDropdownStates((prev) => ({
      ...prev,
      [giftId]: !prev[giftId],
    }));
  }, []);


  const handleViewModeChange = useCallback(
    (mode: ViewMode) => (): void => {
      setViewMode(mode);
    },
    []
  );

  const handleFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>): void => {
      const value = e.target.value as FilterType;
      setFilterValue(value);
      setSelectedFilter(value);
    },
    []
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setSearchTerm(e.target.value);
    },
    []
  );

  const handleStartNewGift = useCallback((): void => {

    // Generate a temporary gift ID for the new gift
    // This can be a temporary ID that gets replaced when the gift is actually saved
    const tempGiftId = `temp_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;


    // Navigate to the new URL structure for gift creation
    navigate(`/my-gifts/create/${tempGiftId}`);
  }, [navigate]);

  // Handle editing a gift (for drafts, navigate to gift creation flow)
  const handleEditGift = useCallback(
    async (giftOrMember: Gift | Member): Promise<void> => {
      if ("_id" in giftOrMember && typeof giftOrMember._id === "string") {
        try {
          // For draft gifts, we navigate to the gift creation flow
          // The gift creation flow should be enhanced to accept a giftId parameter
          // and load the existing draft data

          // Navigate to the new URL structure for editing
          navigate(`/my-gifts/edit/${giftOrMember._id}`);
        } catch (error) {
          console.error("Error navigating to edit gift:", error);
          toast.error("Failed to edit gift");
        }
      }
    },
    [navigate]
  );

  // Draft has every detail filled in — skip the wizard and open on Review, one
  // step from payment. Without ?step the flow would resume from the server's
  // giftsteps and strand the user mid-wizard.
  const handleCheckoutGift = useCallback(
    (gift: Gift): void => {
      navigate(`/my-gifts/edit/${gift._id}?step=5`);
    },
    [navigate]
  );

  // Handle updating gift contact information via API
  const handleUpdateGiftContact = useCallback(
    async (
      giftId: string,
      contactData: UpdateGiftContactRequest
    ): Promise<void> => {
      try {
        const response = await giftItemApi.updateGiftContact(
          giftId,
          contactData
        );

        if (response.status === "Success") {
          toast.success("Gift contact updated successfully");
          // Refresh the gifts list
          fetchGifts(pagination.currentPage);
        } else {
          toast.error(response.message || "Failed to update gift contact");
        }
      } catch (error: any) {
        console.error("Error updating gift contact:", error);
        toast.error(error.message || "Failed to update gift contact");
      }
    },
    [fetchGifts, pagination.currentPage]
  );

  // Handle deleting a gift
  const handleDeleteGift = useCallback(
    async (giftOrMember: Gift | Member): Promise<void> => {
      if ("_id" in giftOrMember && typeof giftOrMember._id === "string") {
        // Show SweetAlert2 confirmation dialog
        const result = await Swal.fire({
          title: "Delete Gift?",
          text: "Are you sure you want to delete this gift? This action cannot be undone.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes, delete it!",
          cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) {
          return; // User cancelled
        }

        try {
          const authToken = localStorage.getItem("authToken");
          const response = await axios.delete(
            `${apiConfig.apiBaseURL}/gift/delete-gift/${giftOrMember._id}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
              },
            }
          );

          if (response.data.status === "Success") {
            // Show success message with SweetAlert2
            await Swal.fire({
              title: "Deleted!",
              text: "Your gift has been deleted successfully.",
              icon: "success",
              confirmButtonColor: "#3085d6",
              timer: 2000,
              showConfirmButton: false,
            });
            // Refresh the gifts list
            fetchGifts(pagination.currentPage);
          } else {
            toast.error(response.data.message || "Failed to delete gift");
          }
        } catch (error: any) {
          console.error("Error deleting gift:", error);
          if (error.response?.data?.message) {
            toast.error(error.response.data.message);
          } else {
            toast.error("Failed to delete gift");
          }
        }
      }
    },
    [fetchGifts, pagination.currentPage]
  );

  // Filter gifts based on search term and filter
  const filteredMembers = useCallback(() => {
    try {
      const currentGifts = getCurrentGifts();

      if (!Array.isArray(currentGifts)) {
        console.error("getCurrentGifts did not return an array:", currentGifts);
        return [];
      }

      const converted = currentGifts
        .map((gift, index) => {
          try {
            return convertGiftToMember(gift);
          } catch (error) {
            console.error(`Error converting gift ${index}:`, error, gift);
            return null;
          }
        })
        .filter(Boolean); // Remove null values


      const filtered = converted.filter((member) => {
        if (!member) return false;

        const matchesFilter = selectedFilter === "" || true; // Implement your filter logic


        return matchesFilter;
      });

      return filtered;
    } catch (error) {
      console.error("Error in filteredMembers:", error);
      return [];
    }
  }, [selectedFilter, getCurrentGifts]);

  const filteredTableMembers = useCallback(() => {
    try {
      const currentGifts = getCurrentGifts();
      if (!Array.isArray(currentGifts)) {
        return [];
      }

      return currentGifts.map(convertGiftToTableMember).filter((member) => {
        if (!member) return false;

        const matchesFilter = selectedFilter === "" || true; // Placeholder for actual filter logic

        return matchesFilter;
      });
    } catch (error) {
      console.error("Error in filteredTableMembers:", error);
      return [];
    }
  }, [selectedFilter, getCurrentGifts]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownStates({});
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close dropdowns when gifts change
  useEffect(() => {
    setDropdownStates({});
  }, [allGifts]);

  // Pagination component
  const PaginationComponent = () => {
    if (pagination.totalPages <= 1) return null;

    const pages: number[] = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(
      1,
      pagination.currentPage - Math.floor(maxVisiblePages / 2)
    );
    let endPage = Math.min(
      pagination.totalPages,
      startPage + maxVisiblePages - 1
    );

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="pagination-wrapper d-flex justify-content-between align-items-center mt-4">
        <div className="pagination-info">
          <span className="text-muted">
            Showing {(pagination.currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(
              pagination.currentPage * itemsPerPage,
              pagination.totalCount
            )}{" "}
            of {pagination.totalCount} gifts
          </span>
        </div>

        <div className="pagination-controls d-flex align-items-center gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            disabled={pagination.currentPage === 1}
            onClick={() => handlePageChange(pagination.currentPage - 1)}
          >
            <FiChevronLeft />
          </Button>

          {startPage > 1 && (
            <>
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handlePageChange(1)}
              >
                1
              </Button>
              {startPage > 2 && <span className="px-2">...</span>}
            </>
          )}

          {pages.map((page) => (
            <Button
              key={page}
              variant={
                page === pagination.currentPage ? "primary" : "outline-primary"
              }
              size="sm"
              onClick={() => handlePageChange(page)}
            >
              {page}
            </Button>
          ))}

          {endPage < pagination.totalPages && (
            <>
              {endPage < pagination.totalPages - 1 && (
                <span className="px-2">...</span>
              )}
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => handlePageChange(pagination.totalPages)}
              >
                {pagination.totalPages}
              </Button>
            </>
          )}

          <Button
            variant="outline-primary"
            size="sm"
            disabled={pagination.currentPage === pagination.totalPages}
            onClick={() => handlePageChange(pagination.currentPage + 1)}
          >
            <FiChevronRight />
          </Button>
        </div>
      </div>
    );
  };

  // Render member card component
  const renderMemberCard = useCallback(
    (member: Member, index: number, isDraft: boolean = false) => (
      <div
        className="members-grid col-xl-3 col-lg-4 col-sm-6 col-6"
        key={member._id || index}
      >
        <div
          className="members-grid-in gift-card-interactive"
          onClick={() => handleShowGiftDetails(member)}
          style={{
            cursor: "pointer",
            position: "relative",
            transition: "all 0.3s ease",
          }}
        >
          {/* Galaxy border effect */}
          <div className="card-border-animation" />
          <figure>
            <GiftCardImage member={member} size="large" />
          </figure>
          <h2 style={{ cursor: "pointer", margin: "15px 0 5px 0" }}>
            {member.name}
            {isDraft ? " (draft)" : ""}
          </h2>
          <em>{member.role}</em>
          <span>{member.type}</span>
          <p>
            <b>{member.date}</b>
          </p>
          <p>{member.frequency}</p>
          <div
            className="member-edit"
            ref={(el) => {
              const dropdownId = member._id || `member-${index}`;
              if (el) {
                dropdownRefs.current[dropdownId] = el;
              }
            }}
          >
            <button
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click
                toggleDropdown(member._id || `member-${index}`);
              }}
              className="edit-toggle"
              aria-label="More options"
            >
              <FiMoreVertical size={18} />
            </button>
            <AnimatePresence>
              {dropdownStates[member._id || `member-${index}`] && (
                <motion.div
                  className="modern-dropdown"
                  initial={{
                    opacity: 0,
                    scale: 0.95,
                    y: -10,
                  }}
                  animate={{
                    opacity: 1,
                    scale: 1,
                    y: 0,
                  }}
                  exit={{
                    opacity: 0,
                    scale: 0.95,
                    y: -10,
                  }}
                  transition={{
                    duration: 0.2,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  }}
                >
                  <motion.div
                    className="modern-dropdown-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleShowGiftDetails(member);
                      setDropdownStates({});
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {/* <span className="icon">👁️</span> */}
                    View Details
                  </motion.div>

                  {/* Only show Edit option for drafts */}
                  {isDraft && (
                    <motion.div
                      className="modern-dropdown-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditGift(member);
                        setDropdownStates({});
                      }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {/* <span className="icon">✏️</span> */}
                      Edit
                    </motion.div>
                  )}

                  <motion.div
                    className="modern-dropdown-item delete-item"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteGift(member);
                      setDropdownStates({});
                    }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <span className="icon">🗑️</span>
                    Delete
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    ),
    [dropdownStates, toggleDropdown, handleShowGiftDetails, handleEditGift]
  );

  // Render table row component
  const renderTableRow = useCallback(
    (member: TableMember, index: number, isDraft: boolean = false) => (
      <tr key={member._id || index}>
        <td>
          <button
            onClick={() => handleShowGiftDetails(member)}
            className="text-decoration-none border-0 bg-transparent p-0"
            style={{ color: "inherit", cursor: "pointer" }}
          >
            {member.title}
          </button>
        </td>
        <td>
          <div className="d-flex align-items-center gap-2 table-profile">
            <div
              style={{
                width: "35px",
                height: "35px",
                borderRadius: "50%",
                overflow: "hidden",
              }}
            >
              <GiftCardImage
                member={member}
                size="small"
                className="rounded-circle"
              />
            </div>
            <span>{member.name}</span>
          </div>
        </td>
        <td>{member.role}</td>
        <td>{member.type}</td>
        <td>{member.date}</td>
        <td>{member.frequency}</td>
        <td className="table-actions">
          <div className="d-flex align-items-center gap-2">
            <button
              onClick={() => handleShowGiftDetails(member)}
              className="btn btn-sm btn-outline-primary border-0 p-1"
              title="View Details"
              aria-label="View Details"
            >
              <FiEye size={14} />
            </button>
            {/* Only show Edit option for drafts */}
            {isDraft && (
              <button
                onClick={() => handleEditGift(member)}
                className="btn btn-sm btn-outline-secondary border-0 p-1"
                title="Edit"
                aria-label="Edit"
              >
                <FiEdit2 size={14} />
              </button>
            )}
            <button
              onClick={() => handleDeleteGift(member)}
              className="btn btn-sm btn-outline-danger border-0 p-1"
              title="Delete"
              aria-label="Delete"
            >
              <FiTrash2 size={14} />
            </button>
          </div>
        </td>
      </tr>
    ),
    [handleShowGiftDetails, handleEditGift, handleDeleteGift]
  );

  // Render get started section with different content based on tab
  const renderGetStartedSection = useCallback(
    (tabType: "gifts" | "draft" | "past") => {
      const getContentByTab = () => {
        switch (tabType) {
          case "gifts":
            return {
              title: "Start Your Legacy",
              description: "Leave a legacy of encouragement and support for those you care about.",
              buttonText: "Let's get started",
            };
          case "draft":
            return {
              title: "No Gifts in Drafts.",
              description: "Leave a legacy with unforgettable gifts.",
              buttonText: "Create Draft",
            };
          case "past":
            return {
              title: "Start Your Legacy",
              description: "Celebrate the beginning with unforgettable gifts.",
              buttonText: "Start First Gift",
            };
          default:
            return {
              title: "Start Your Legacy",
              description: "Celebrate the beginning with unforgettable gifts.",
              buttonText: "Let's get started",
            };
        }
      };

      const content = getContentByTab();

      return (
        <motion.div
          className="container bg-white member-area"
          initial={{ opacity: 0, y: 30 }}
          animate={{
            opacity: 1,
            y: 0,
            transition: {
              duration: 0.6,
              ease: [0.25, 0.46, 0.45, 0.94],
            },
          }}
        >
          <div className="get-started-sec">
            <div className="get-started-sec-in">
              <motion.div
                className="content-box"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  transition: {
                    delay: 0.2,
                    duration: 0.5,
                    ease: [0.25, 0.46, 0.45, 0.94],
                  },
                }}
              >
                <motion.div
                  className="success-icon-container"
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{
                    delay: 0.4,
                    duration: 0.6,
                    type: "spring",
                    stiffness: 300,
                    damping: 25,
                  }}
                >
                  {/* Wave Animation Layers */}
                  
                  

                  {/* Main GiftBox Circle */}
                 
                    <img src={GiftBox} alt="Gift Box" />
                </motion.div>
                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 0.6,
                      duration: 0.4,
                    },
                  }}
                >
                  {content.title}
                </motion.h2>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 0.7,
                      duration: 0.4,
                    },
                  }}
                >
                  {content.description}
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: {
                      delay: 0.8,
                      duration: 0.4,
                    },
                  }}
                >
                  {/* <motion.div
                    whileHover={{
                      scale: 1.05,
                      boxShadow: "0 8px 25px rgba(57, 87, 160, 0.3)",
                      transition: { duration: 0.2 },
                    }}
                    whileTap={{
                      scale: 0.95,
                      transition: { duration: 0.1 },
                    }}
                  > */}
                    <Button className="bttn" onClick={handleStartNewGift}>
                      {content.buttonText}
                    </Button>
                  {/* </motion.div> */}
                </motion.div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      );
    },
    [handleStartNewGift]
  );

  // Render main content section
  const renderMainContent = useCallback(
    (isDraft: boolean = false) => {
      const currentGifts = getCurrentGifts();
      const filteredGifts = filteredMembers();

      //     loading,
      //     pagination
      // });

      // Get custom loading text based on current tab
      const getLoadingText = () => {
        switch (giftTab) {
          case "1":
            return "Loading your heartfelt gifts...";
          case "2":
            return "Gathering your draft gifts...";
          case "3":
            return "Retrieving your delivered gifts...";
          default:
            return "Loading your special gifts...";
        }
      };

      return (
        <div className="container bg-white member-area">
          <div className="profile-sec">
              <div className="profile-filter">
                <div className="d-flex justify-content-between">
                  <aside className="search">
                    <div className="search-box">
                      <input
                        type="text"
                        placeholder="Search"
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      <span className="icon">
                        <img src={SearchIcon} alt="Search" />
                      </span>
                    </div>
                  </aside>

                  <aside className="filter">
                    {/* <FormGroup className='form-group m-0'>
                                      <Form.Select value={filterValue} onChange={handleFilterChange}>
                                          <option value="">Filter By</option>
                                          <option value="friend">Friend</option>
                                          <option value="family">Family</option>
                                          <option value="socialMedia">Social Media</option>
                                          <option value="advertisement">Advertisement</option>
                                          <option value="other">Other</option>
                                      </Form.Select>
                                  </FormGroup> */}
                    <div className="filter-grid">
                      <Link
                        to="#"
                        className={viewMode === "grid" ? "active" : ""}
                        onClick={(e) => {
                          e.preventDefault();
                          setViewMode("grid");
                        }}
                      >
                        <img src={GridIcon} alt="Grid View" />
                      </Link>
                      <Link
                        to="#"
                        className={viewMode === "list" ? "active" : ""}
                        onClick={(e) => {
                          e.preventDefault();
                          setViewMode("list");
                        }}
                      >
                        <img src={ListIcon} alt="List View" />
                      </Link>
                    </div>
                  </aside>

                  <Dropdown
                    align="end"
                    className="filter-dropdown d-block d-lg-none"
                  >
                    <Dropdown.Toggle
                      as="aside"
                      className="filter-mbl border-0 bg-transparent p-0"
                      id="dropdown-filter"
                    >
                      <figure className="mb-0">
                        <img src={FilterIcon} alt="Filter" />
                      </figure>
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="p-3" style={{ minWidth: "180px" }}>
                      <div className="d-flex gap-2">
                        <Dropdown.Item
                          as="button"
                          onClick={handleViewModeChange("grid")}
                          className={`d-flex align-items-center justify-content-center gap-2 view-mode-btn ${
                            viewMode === "grid" ? "active" : ""
                          }`}
                        >
                          <img src={GridIcon} alt="Grid View" />
                          <span>Grid</span>
                        </Dropdown.Item>
                        <Dropdown.Item
                          as="button"
                          onClick={handleViewModeChange("list")}
                          className={`d-flex align-items-center justify-content-center gap-2 view-mode-btn ${
                            viewMode === "list" ? "active" : ""
                          }`}
                        >
                          <img src={ListIcon} alt="List View" />
                          <span>List</span>
                        </Dropdown.Item>
                      </div>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </div>

          <section className="members-grid-area">
            {loading && !isInitialLoad ? (
              // Show loading state only for content area (not initial load)
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: "400px" }}
              >
                <GiftLoader
                  text={getLoadingText()}
                  size="medium"
                  showText={true}
                />
              </div>
            ) : viewMode === "grid" ? (
              <AnimatePresence>
                <motion.div
                  className="row"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {filteredGifts.length > 0 ? (
                    filteredGifts.map((member, index) => (
                      <motion.div
                        key={member._id || index}
                        className="members-grid col-xl-3 col-lg-4 col-sm-6 col-6"
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{
                          opacity: 1,
                          y: 0,
                          scale: 1,
                          transition: {
                            delay: index * 0.1,
                            duration: 0.4,
                            ease: [0.25, 0.46, 0.45, 0.94],
                          },
                        }}
                        exit={{
                          opacity: 0,
                          y: -20,
                          scale: 0.95,
                          transition: { duration: 0.3 },
                        }}
                        whileHover={{
                          y: -8,
                          scale: 1.02,
                          transition: { duration: 0.2 },
                        }}
                        layout
                      >
                        <div
                          className="members-grid-in gift-card-interactive"
                          onClick={() => handleShowGiftDetails(member)}
                          style={{
                            cursor: "pointer",
                            position: "relative",
                            transition: "all 0.3s ease",
                          }}
                        >
                          {/* Galaxy border effect */}
                          <div className="card-border-animation" />
                          <figure>
                            <GiftCardImage member={member} size="large" />
                          </figure>
                          <h2
                            style={{
                              cursor: "pointer",
                              margin: "15px 0 5px 0",
                            }}
                          >
                            {member.name}
                            {isDraft ? " " : ""}
                          </h2>
                          <em>{member.role}</em>
                          <span>{member.type}</span>
                          <p>
                            <b>{member.date}</b>
                          </p>
                          <p>{member.frequency}</p>
                          <div
                            className="member-edit"
                            ref={(el) => {
                              const dropdownId =
                                member._id || `member-${index}`;
                              if (el) {
                                dropdownRefs.current[dropdownId] = el;
                              }
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card click
                                toggleDropdown(member._id || `member-${index}`);
                              }}
                              className="edit-toggle"
                              aria-label="More options"
                            >
                              <FiMoreVertical size={18} />
                            </button>
                            <AnimatePresence>
                              {dropdownStates[
                                member._id || `member-${index}`
                              ] && (
                                <motion.div
                                  className="modern-dropdown"
                                  initial={{
                                    opacity: 0,
                                    scale: 0.95,
                                    y: -10,
                                  }}
                                  animate={{
                                    opacity: 1,
                                    scale: 1,
                                    y: 0,
                                  }}
                                  exit={{
                                    opacity: 0,
                                    scale: 0.95,
                                    y: -10,
                                  }}
                                  transition={{
                                    duration: 0.2,
                                    ease: [0.25, 0.46, 0.45, 0.94],
                                  }}
                                >
                                  <motion.div
                                    className="modern-dropdown-item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleShowGiftDetails(member);
                                      setDropdownStates({});
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                  >
                                    {/* <span className="icon">👁️</span> */}
                                    View Details
                                  </motion.div>

                                  {/* Only show Edit option for drafts */}
                                  {isDraft && (
                                    <motion.div
                                      className="modern-dropdown-item"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditGift(member);
                                        setDropdownStates({});
                                      }}
                                      whileTap={{ scale: 0.98 }}
                                    >
                                      {/* <span className="icon">✏️</span> */}
                                      Edit
                                    </motion.div>
                                  )}

                                  <motion.div
                                    className="modern-dropdown-item delete-item"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteGift(member);
                                      setDropdownStates({});
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                  >
                                    {/* <span className="icon">🗑️</span> */}
                                    Delete
                                  </motion.div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        </div>
                      </motion.div>
                    ))
                  ) : !loading ? (
                    <div className="col-12">
                      <NoGiftsFound
                        isDraft={isDraft}
                        searchTerm={searchTerm}
                        selectedFilter={selectedFilter}
                        onClearFilters={() => {
                          setSearchTerm("");
                          setSelectedFilter("");
                          setFilterValue("");
                        }}
                      />
                    </div>
                  ) : null}
                </motion.div>
              </AnimatePresence>
            ) : (
              <motion.div
                className="table-container"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <Table responsive hover className="gifts-table">
                  <thead>
                    <tr>
                      <th>Gift Title</th>
                      <th>Recipient Name</th>
                      <th>Relationship</th>
                      <th>Occasion</th>
                      <th>Date</th>
                      <th>Gift Type</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence>
                      {filteredTableMembers().length > 0 ? (
                        filteredTableMembers().map((member, index) => (
                          <motion.tr
                            key={member._id || index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{
                              opacity: 1,
                              x: 0,
                              transition: {
                                delay: index * 0.05,
                                duration: 0.3,
                              },
                            }}
                            exit={{ opacity: 0, x: 20 }}
                            whileHover={{
                              backgroundColor: "rgba(57, 87, 160, 0.05)",
                            }}
                          >
                            <td>
                              <button
                                onClick={() => handleShowGiftDetails(member)}
                                className="text-decoration-none border-0 bg-transparent p-0 gift-title-btn"
                                style={{
                                  color: "#677289",
                                  cursor: "pointer",
                                  fontWeight: "500",
                                }}
                              >
                                {member.title}
                              </button>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-2 table-profile">
                                <div
                                  style={{
                                    width: "35px",
                                    height: "35px",
                                    borderRadius: "18px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <GiftCardImage
                                    member={member}
                                    size="small"
                                    className="table-profile-image"
                                  />
                                </div>
                                <span>{member.name}</span>
                              </div>
                            </td>
                            <td>{member.role}</td>
                            <td>{member.type}</td>
                            <td>{member.date}</td>
                            <td>{member.frequency}</td>
                            <td className="table-actions">
                              <div className="d-flex align-items-center gap-2">
                                <button
                                  onClick={() => handleShowGiftDetails(member)}
                                  className="btn btn-sm btn-outline-primary border-0 p-1"
                                  title="View Details"
                                  aria-label="View Details"
                                >
                                  <FiEye size={14} />
                                </button>
                                {/* Only show Edit option for drafts */}
                                {giftTab === "2" && (
                                  <button
                                    onClick={() => handleEditGift(member)}
                                    className="btn btn-sm btn-outline-secondary border-0 p-1"
                                    title="Edit"
                                    aria-label="Edit"
                                  >
                                    <FiEdit2 size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={() => handleDeleteGift(member)}
                                  className="btn btn-sm btn-outline-danger border-0 p-1"
                                  title="Delete"
                                  aria-label="Delete"
                                >
                                  <FiTrash2 size={14} />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))
                      ) : !loading ? (
                        <tr>
                          <td colSpan={7} className="p-0">
                            <NoGiftsFound
                              isDraft={giftTab === "2"}
                              searchTerm={searchTerm}
                              selectedFilter={selectedFilter}
                              onClearFilters={() => {
                                setSearchTerm("");
                                setSelectedFilter("");
                                setFilterValue("");
                              }}
                            />
                          </td>
                        </tr>
                      ) : null}
                    </AnimatePresence>
                  </tbody>
                </Table>
              </motion.div>
            )}
          </section>

          {/* Add Pagination Component */}
          <PaginationComponent />
        </div>
      );
    },
    [
      searchTerm,
      handleSearchChange,
      filterValue,
      handleFilterChange,
      viewMode,
      handleViewModeChange,
      filteredMembers,
      filteredTableMembers,
      renderMemberCard,
      renderTableRow,
      loading,
      getCurrentGifts,
      allGifts.length,
      pagination,
    ]
  );

  // Check if current tab should show get started
  const shouldShowGetStarted = useCallback(() => {
    const currentGifts = getCurrentGifts();
    // Only show get started if there are no gifts AND no active search/filter
    return currentGifts.length === 0 && !loading && !searchTerm.trim() && !selectedFilter;
  }, [getCurrentGifts, loading, searchTerm, selectedFilter]);

  const getTabType = useCallback(() => {
    switch (giftTab) {
      case "1":
        return "gifts";
      case "2":
        return "draft";
      case "3":
        return "past";
      default:
        return "gifts";
    }
  }, [giftTab]);

  return (
    <div style={{ overflowX: "hidden", width: "100%" }}>
      <Header />

      {isInitialLoad && loading ? (
        // Show full page loader only on initial load
        <section
          className={`main-layout ${location.pathname === '/my-contacts' ? 'my-contacts-page' : ''}`}
          data-active-tab={activeTab}
          style={{ overflowX: "hidden" }}
        >
          <div className="container bg-white member-area">
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ minHeight: "400px" }}
            >
              <GiftLoader
                text="Loading your special gifts..."
                size="medium"
                showText={true}
              />
            </div>
          </div>
        </section>
      ) : (
        <section
          className={`main-layout ${location.pathname === '/my-contacts' ? 'my-contacts-page' : ''}`}
          data-active-tab={activeTab}
          style={{ overflowX: "hidden" }}
        >
          <AnimatePresence mode="wait">
            {activeTab === "1" && (
            <motion.div
              key="gifts-tab"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              style={{ overflowX: "hidden" }}
            >
              <div
                className="gifts-body h-100 py"
                style={{ overflowX: "hidden" }}
              >
                <div
                  className="gifts-body-in py-0"
                  style={{ overflowX: "hidden" }}
                >
                  <AnimatePresence mode="wait">
                    {/* Show GiftDetails if a gift is selected */}
                    {showGiftDetails && selectedGiftDetails ? (
                      <motion.div
                        key="gift-details"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                      >
                        <GiftDetails
                          gift={selectedGiftDetails}
                          onBack={handleBackToGifts}
                          onEdit={handleEditGift}
                          onCheckout={handleCheckoutGift}
                          giftItemsCache={giftItemsCache}
                        />
                      </motion.div>
                    ) : showGigStart ? (
                      <motion.div
                        key="start-gift"
                        className="h-100"
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -30 }}
                        transition={{ duration: 0.4 }}
                      >
                        <StartGift standalone={false} />
                      </motion.div>
                    ) : (
                      <>
                        <motion.section
                          className="top-strip"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{
                            opacity: 1,
                            y: 0,
                            transition: {
                              duration: 0.4,
                              ease: [0.25, 0.46, 0.45, 0.94],
                            },
                          }}
                        >
                          <div className="container">
                            <div className="d-flex justify-content-between">
                              <motion.aside
                                className="top-strip-left"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{
                                  opacity: 1,
                                  x: 0,
                                  transition: {
                                    delay: 0.1,
                                    duration: 0.3,
                                  },
                                }}
                              >
                                <Tabs
                                  activeKey={giftTab}
                                  onSelect={(k) => k && toggleGiftTab(k)}
                                  className="my-gifts-tab"
                                >
                                  <Tab
                                    eventKey="1"
                                    title="My Gifts"
                                    className="custom-tab-bg"
                                  ></Tab>
                                  <Tab
                                    eventKey="2"
                                    title="Draft"
                                    className="custom-tab-bg"
                                  ></Tab>
                                  <Tab
                                    eventKey="3"
                                    title="Past"
                                    className="custom-tab-bg"
                                  ></Tab>
                                </Tabs>
                              </motion.aside>
                              {/* Only show "Start New Gift" button when there are gifts in the current tab */}
                              {!shouldShowGetStarted() && !loading && (
                                <motion.aside
                                  className="top-strip-right"
                                  initial={{ opacity: 0, x: 20 }}
                                  animate={{
                                    opacity: 1,
                                    x: 0,
                                    transition: {
                                      delay: 0.2,
                                      duration: 0.3,
                                    },
                                  }}
                                  exit={{
                                    opacity: 0,
                                    x: 20,
                                    transition: { duration: 0.2 },
                                  }}
                                >
                                  <motion.div
                                    whileHover={{
                                      scale: 1.05,
                                      // boxShadow:
                                      //   "0 8px 25px rgba(57, 87, 160, 0.3)",
                                      transition: { duration: 0.2 },
                                    }}
                                    whileTap={{
                                      scale: 0.95,
                                      transition: { duration: 0.1 },
                                    }}
                                  >
                                    <Button
                                      className="bttn"
                                      onClick={handleStartNewGift}
                                    >
                                      Start New Gift
                                    </Button>
                                  </motion.div>
                                </motion.aside>
                              )}
                            </div>
                          </div>
                        </motion.section>

                        <AnimatePresence mode="wait">
                          {giftTab === "1" && (
                            <motion.div
                              key="my-gifts-content"
                              initial={{ opacity: 0, x: 20 }}
                              animate={{
                                opacity: 1,
                                x: 0,
                                transition: {
                                  duration: 0.3,
                                  ease: [0.25, 0.46, 0.45, 0.94],
                                },
                              }}
                              exit={{
                                opacity: 0,
                                x: -20,
                                transition: { duration: 0.2 },
                              }}
                              style={{ overflowX: "hidden" }}
                            >
                              {shouldShowGetStarted()
                                ? renderGetStartedSection(getTabType())
                                : renderMainContent(false)}
                            </motion.div>
                          )}

                          {giftTab === "2" && (
                            <motion.div
                              key="drafts-content"
                              initial={{ opacity: 0, x: 20 }}
                              animate={{
                                opacity: 1,
                                x: 0,
                                transition: {
                                  duration: 0.3,
                                  ease: [0.25, 0.46, 0.45, 0.94],
                                },
                              }}
                              exit={{
                                opacity: 0,
                                x: -20,
                                transition: { duration: 0.2 },
                              }}
                              style={{ overflowX: "hidden" }}
                            >
                              {shouldShowGetStarted()
                                ? renderGetStartedSection(getTabType())
                                : renderMainContent(true)}
                            </motion.div>
                          )}

                          {giftTab === "3" && (
                            <motion.div
                              key="past-content"
                              initial={{ opacity: 0, x: 20 }}
                              animate={{
                                opacity: 1,
                                x: 0,
                                transition: {
                                  duration: 0.3,
                                  ease: [0.25, 0.46, 0.45, 0.94],
                                },
                              }}
                              exit={{
                                opacity: 0,
                                x: -20,
                                transition: { duration: 0.2 },
                              }}
                              style={{ overflowX: "hidden" }}
                            >
                              {shouldShowGetStarted()
                                ? renderGetStartedSection(getTabType())
                                : renderMainContent(false)}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          )}
          {activeTab === "2" && (
            <motion.div
              key="contacts-tab"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              style={{ overflowX: "hidden" }}
            >
              <MyContracts />
            </motion.div>
          )}
        </AnimatePresence>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default GiftsMessages;
